import type { ErrorComponentProps } from "@tanstack/react-router"
import { Link, rootRouteId, useMatch, useRouter } from "@tanstack/react-router"
import { AlertCircleIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { usePostHog } from "posthog-js/react"
import { useEffect, useState } from "react"

import { Button, buttonVariants } from "@repo/ui/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui/components/ui/collapsible"
import { logger } from "@repo/utils/logger"
import { sharedConfig } from "~/lib/config/shared"

const log = logger.child({ module: "components/default-catch-boundary" })

export function DefaultCatchBoundary({ error, info }: ErrorComponentProps) {
  const router = useRouter()
  const isRoot = useMatch({
    strict: false,
    select: (state) => state.id === rootRouteId,
  })
  const posthog = usePostHog()
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    log.error("DefaultCatchBoundary", { error, isRoot, info })
    posthog?.captureException(error, { isRoot, info })
  }, [error, isRoot, info, posthog])

  const errorMessage =
    error instanceof Error
      ? error.message || "An unexpected error occurred. Please try again."
      : "An unexpected error occurred. Please try again."

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="relative z-10 flex h-screen w-full flex-col items-center justify-center p-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 15,
        }}
      >
        <Card className="glass w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircleIcon className="h-8 w-8 text-red-400" />
            </div>
            <CardTitle className="text-2xl text-white">
              An unexpected error occurred
            </CardTitle>
            <CardDescription className="mt-2 text-white/80">
              {errorMessage}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                variant="default"
                onClick={() => {
                  router.invalidate()
                }}
                className="min-w-[120px]"
              >
                Try Again
              </Button>
              {isRoot ? (
                <Link
                  to="/"
                  className={buttonVariants({
                    variant: "secondary",
                    className: "min-w-[120px]",
                  })}
                >
                  Home
                </Link>
              ) : (
                <Link
                  to="/"
                  className={buttonVariants({
                    variant: "secondary",
                    className: "min-w-[120px]",
                  })}
                  onClick={(e) => {
                    if (window.history.length > 1) {
                      e.preventDefault()
                      window.history.back()
                    }
                  }}
                >
                  Go Back
                </Link>
              )}
            </div>
          </CardContent>
          {sharedConfig.appEnv !== "production" && (
            <CardFooter className="flex-col">
              <Collapsible
                open={showDetails}
                onOpenChange={setShowDetails}
                className="w-full"
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between text-white/70 hover:text-white"
                  >
                    <span>Technical Details</span>
                    {showDetails ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-3 rounded-lg border border-white/20 bg-black/30 p-4 font-mono text-white/90 text-xs">
                    <div className="mb-3 font-semibold text-white">Error:</div>
                    <div className="mb-4 whitespace-pre-wrap break-words">
                      {error instanceof Error
                        ? error.stack || error.toString()
                        : String(error)}
                    </div>
                    {info && (
                      <>
                        <div className="mt-4 mb-3 font-semibold text-white">
                          Component Stack:
                        </div>
                        <div className="whitespace-pre-wrap break-words">
                          {info.componentStack}
                        </div>
                      </>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </CardFooter>
          )}
        </Card>
      </motion.div>
    </AnimatePresence>
  )
}
